import getDefaultPac from '../../util/aactools/getDefaultPAC';
import { ActionCreators as UndoActionCreators } from 'redux-undo';
import {
  actionAddItem,
  actionAddMultiple,
  actionHideItem,
  actionLoadPac,
  actionRemoveItem,
  actionSetUseMountingRails,
  actionSetWall,
  actionUpdateItem,
  actionUpdateMultiple,
} from './tacActions';
import constants from '../../settings/constants';
import {
  selectCeilingHeightBasedOnMarket,
  selectTac,
  selectWallPoints,
  selectWallSize,
} from './tacSelectors';
import {
  selectShouldFilterIntroPopupBeVisible,
  selectErrorHandlingPendingTimestamp,
} from '../popups/popupsSelectors';
import {
  actionSetFilterIntroPopupBeVisible,
  actionSetHasShownFilterIntroPopup,
  actionShowSceneErrors,
} from '../popups';
import { actionSetDirtyConfiguration } from '../vpc/vpcActions';
import convert from '../../util/aactools/convert';
import { tacSetToastFlags } from './tacActions';
import { selectSprs } from '../sprs/sprsSelectors.ts';
import { openPacMissing } from '../dialog/dialogActions';
import {
  actionSetOverlayState,
  actionSetErrorHandlingPendingTimestamp,
} from '../popups/popupsActions';
import getWallPoints from './tacReducer/getWallPoints';
import { thunkSetMargins } from '../scene/sceneThunks';
import { getWallHeightDependentItems } from '../../services/products';
import { thunkSetView } from '../navigation/navigationThunks';
import { selectCurrentView } from '../navigation/navigationSelectors';
import { actionClearFilter } from '../productMenu/productMenuActions';
import history from '../../services/history';
import { isFixedRoom } from '../../util/room';
import validate, { MeansOfNotification } from './tacReducer/validate';
import addItem from './tacReducer/addItem';
import updateDependentItems from './tacReducer/updateDependentItems';
import { addMultipleItems } from './tacReducer/addMultipleItems';
import tacHelpers from './tacHelpers';
import updateItem from './tacReducer/updateItem';
import { updateMultipleItems } from './tacReducer/updateMultipleItems';
import removeItem from './tacReducer/removeItem';
import hideItem from './tacReducer/hideItem';
import {
  thunkHandlePopupOnAddItem,
  thunkHandlePopupOnUpdateItem,
  thunkHandlePopupOnRemoveItem,
  thunkHandlePopupOnTacLoad,
  thunkHandleMountingRailPopup,
} from '../popups/popupsThunks';
import { actionSetDraftCeilingHeight } from '../draft/ceilingHeight/ceilingHeightActions';
import { getCuredHeight } from '../draft/ceilingHeight/ceilingHeightThunks';
import { thunkShowMultipackMessageIfNeededFor } from '../toasts/toastsThunks';
import { clearHasShownMultipackMessageFor } from '../toasts/toastsActions';
import toastMaster from '../../services/toastMaster';
import { AmeliorationOutcome } from '../../services/amelioration/ameliorationTypes';
import { POINTER_DOWN, KEY_DOWN } from '../../util/supportedEvents';
import localStatisticsReporter from '../../services/statistics/insights/custom/local/localStatisticsReporter';

const thunkSetToastFlags = pac => (dispatch, getState) => {
  /**
   * If the pac has an id it means that its actually an spr.
   * @returns {string | undefined}
   */
  const pacIsSpr = () => {
    return pac.id;
  };

  /**
   * Extra items are only possible to have in the app when they are loaded from the cms.
   * This function checks if there is an spr that has an extra item.
   * @returns
   */
  const sprContainsExtraItems = () => {
    if (!pacIsSpr()) return;

    const sprs = selectSprs(getState());
    const spr = sprs.find(spr => spr.id === pac.id);
    return spr.model?.extraItems?.length;
  };

  const tac = convert.PACtoTAC(pac);
  const toastFlags = convert.getToastFlaggedTac(
    tac,
    pac,
    sprContainsExtraItems()
  );
  dispatch(tacSetToastFlags(toastFlags));
};

/**
 * Thunk for loading pac
 * @param pac
 * @returns {function(*): void}
 */
export const thunkLoadPac = pac => (dispatch, getState) => {
  const tac = convert.PACtoTAC(pac);

  const getDraftHeight = () =>
    selectCeilingHeightBasedOnMarket(getState(), tac.wall.size.height);

  const draftHeight = tac.wall && getDraftHeight();

  tac && history.save(convert.TACtoPAC(tac));

  dispatch(actionLoadPac(tac, validate(tac), !isFixedRoom()));
  tac.wall && dispatch(actionSetDraftCeilingHeight(draftHeight));
  dispatch(thunkSetToastFlags(pac));
  dispatch(UndoActionCreators.clearHistory());
  dispatch(thunkHandlePopupOnTacLoad());
  dispatch(clearHasShownMultipackMessageFor());
  const currentView = selectCurrentView(getState());
  const newView =
    currentView === constants.VIEW_NAMES.START
      ? constants.VIEW_NAMES.SCENE
      : currentView;
  dispatch(thunkSetView(newView, { nonInteraction: true }));
  dispatch(actionSetDirtyConfiguration(true));
  dispatch(thunkHandleMountingRailPopup({ tac }));
};

/**
 * Thunk for resetting scene
 *
 * @returns {function(*): void}
 */
export const thunkResetScene = () => dispatch => {
  dispatch(actionClearFilter());
  dispatch(thunkLoadPac(getDefaultPac()));
};

/**
 * Thunk to handle missing pac
 *
 * @returns {function(*): void}
 */

export const thunkOpenPacMissing = () => dispatch => {
  dispatch(thunkSetView(constants.VIEW_NAMES.START, { nonInteraction: true }));
  dispatch(actionSetOverlayState(true));
  dispatch(openPacMissing());
};

/**
 * Thunk for loading spr
 *
 * @param spr
 * @returns {function(*): *}
 */
export const thunkLoadSpr = spr => dispatch => {
  dispatch(actionClearFilter());
  dispatch(thunkLoadPac(spr));
};

/**
 * Generic tac updater
 * @returns {function(...[*]): function(*, *): void}
 */
export const genericTacUpdaterThunk = () => (dispatch, getState) => {
  dispatch(actionSetDirtyConfiguration(true));

  if (selectShouldFilterIntroPopupBeVisible(getState())) {
    dispatch(actionSetFilterIntroPopupBeVisible(true));
    dispatch(actionSetHasShownFilterIntroPopup(true));
  }
};

/**
 * Add item
 * @param item
 * @param parent
 * @param meta
 * @returns {(function(*, *): void)|*}
 */
export const thunkAddItem =
  (item, parent, meta = {}) =>
  (dispatch, getState) => {
    const tac = selectTac(getState());
    const model = addItem(tac, item, parent, { isPersistent: true });
    const newModel =
      model &&
      updateDependentItems(model, {
        isPersistent: true,
        triggerItem: item,
      });
    const errors = validate(newModel);

    newModel && history.save(convert.TACtoPAC(newModel));

    dispatch(actionAddItem(newModel, errors, meta));
    dispatch(thunkShowMultipackMessageIfNeededFor(item.id));
    dispatch(genericTacUpdaterThunk());
    const openedExtendableConf = !!dispatch(
      thunkHandlePopupOnAddItem(item, meta)
    );
    !openedExtendableConf &&
      dispatch(thunkHandleMountingRailPopup({ tac: newModel }));
  };

/**
 * Add multiple
 * @param items
 * @param parent
 * @param meta
 * @returns {(function(*, *): void)|*}
 */
export const thunkAddMultiple =
  (items, parent, meta = {}) =>
  (dispatch, getState) => {
    const tac = selectTac(getState());
    const model = addMultipleItems(tac, items, parent, {
      isPersistent: true,
    });
    const errors = validate(model);

    model && history.save(convert.TACtoPAC(model));

    dispatch(actionAddMultiple(model, errors, meta));
    items.forEach(item =>
      dispatch(thunkShowMultipackMessageIfNeededFor(item.id))
    );
    dispatch(genericTacUpdaterThunk());
  };

/**
 * Update item
 * @param item
 * @param parent
 * @param meta
 * @returns {(function(*, *): void)|*}
 */
export const thunkUpdateItem = (item, parent, meta) => (dispatch, getState) => {
  const tac = selectTac(getState());
  const originalItem = tacHelpers.getItem(tac, item.itemid);

  const model = updateItem(tac, item, parent, {
    moveOthers: meta && meta.moveOthers,
    isPersistent: true,
  });

  const newModel =
    model &&
    updateDependentItems(model, {
      isPersistent: true,
      priorityItem: meta && meta.priorityItem,
      triggerItem: originalItem,
    });
  const errors = validate(newModel);

  newModel && history.save(convert.TACtoPAC(newModel));

  dispatch(actionUpdateItem(newModel, errors, meta));
  dispatch(thunkShowMultipackMessageIfNeededFor(item.id));
  dispatch(genericTacUpdaterThunk());
  const openedExtendableConf = !!dispatch(
    thunkHandlePopupOnUpdateItem(item, meta)
  );
  !openedExtendableConf &&
    dispatch(thunkHandleMountingRailPopup({ tac: newModel }));
};

/**
 * Update multiple
 * @param items
 * @param parent
 * @param meta
 * @returns {(function(*, *): void)|*}
 */
export const thunkUpdateMultiple =
  (items, parent, meta = {}) =>
  (dispatch, getState) => {
    const tac = selectTac(getState());
    const model = updateMultipleItems(tac, items, parent, meta);

    const newModel =
      model &&
      updateDependentItems(model, {
        isPersistent: true,
        priorityItem: meta && meta.priorityItem,
        triggerItem: meta?.triggerItem,
      });
    const errors = validate(newModel);

    newModel && history.save(convert.TACtoPAC(newModel));

    dispatch(actionUpdateMultiple(newModel, errors, meta));
    items.forEach(item =>
      dispatch(thunkShowMultipackMessageIfNeededFor(item.id))
    );
    dispatch(thunkHandleMountingRailPopup({ tac: newModel }));
    dispatch(genericTacUpdaterThunk());
  };

/**
 * Remove item
 * @param item
 * @returns {(function(*, *): void)|*}
 */
export const thunkRemoveItem =
  (item, meta = {}) =>
  (dispatch, getState) => {
    const tac = selectTac(getState());
    let model = removeItem(tac, item);

    model =
      model &&
      updateDependentItems(model, {
        isPersistent: true,
        triggerItem: item,
      });

    if (model) {
      tacHelpers
        .getAllItems(model.items, item)
        .filter(item => item.connectsTo === item.itemid)
        .forEach(dependency => {
          model = removeItem(model, dependency);
        });
    }

    model && history.save(convert.TACtoPAC(model));
    const errors = validate(model);

    dispatch(actionRemoveItem(model, errors, meta));
    dispatch(thunkHandleMountingRailPopup({ tac: model }));
    dispatch(thunkHandlePopupOnRemoveItem());
    dispatch(genericTacUpdaterThunk());
  };

/**
 * Hide item
 * @param item
 * @param parent
 * @returns {(function(*, *): void)|*}
 */
export const thunkHideItem =
  (item, parent, meta = {}) =>
  (dispatch, getState) => {
    const tac = selectTac(getState());
    const model = hideItem(tac, item, parent);
    const errors = validate(model);

    model && history.save(convert.TACtoPAC(model));

    dispatch(actionHideItem(model, errors, meta));
    dispatch(genericTacUpdaterThunk());
  };

/**
 * Thunk set using mounting rail
 * @param disableMountingRails
 * @returns {(function(*, *): void)|*}
 */
export const thunkSetUsingMountingRail =
  disableMountingRails => (dispatch, getState) => {
    const tac = selectTac(getState());

    const model = {
      ...tac,
      settings: {
        ...tac.settings,
        disableMountingRails: disableMountingRails,
      },
    };

    const newModel = updateDependentItems(model, {
      isPersistent: true,
    });

    newModel && history.save(convert.TACtoPAC(newModel));
    dispatch(actionSetUseMountingRails(newModel));
    dispatch(thunkHandleMountingRailPopup({ tac: newModel }));
  };

/**
 * Converts the state Tac to Pac and saves it to local storage.
 * @definition Tac | Temporary article configuration - Saved in redux
 * @definition Pac | Permanent article configuration - Saved in local storage
 * @param {*} state
 */
const saveStateToLocalStorage = state => {
  const tac = selectTac(state);
  history.save(convert.TACtoPAC(tac));
};

export const thunkRedo = () => (dispatch, getState) => {
  dispatch(UndoActionCreators.redo());
  saveStateToLocalStorage(getState());
  dispatch(actionSetDirtyConfiguration(true));
};

export const thunkUndo = () => (dispatch, getState) => {
  dispatch(UndoActionCreators.undo());
  saveStateToLocalStorage(getState());
  dispatch(actionSetDirtyConfiguration(true));
};

/**
 * Thunk for replacing the current tac with a new one
 * @param tac The new tac
 * @returns {function(*): void}
 */
export const thunkReplaceTac = tac => (dispatch, getState) => {
  dispatch(actionLoadPac(tac, validate(tac), !isFixedRoom()));
  dispatch(genericTacUpdaterThunk());
  saveStateToLocalStorage(getState());
};

const thunkMoveWallHeightDependentItems = moveInYDirection => dispatch => {
  const getMovedWallHeightDependingItem = item => {
    return {
      ...item,
      y: item.y + moveInYDirection,
    };
  };
  const replaceMovedPartsInSection = (section, movedParts) => {
    return section.items.reduce((acc, curr) => {
      const findPart = movedParts.find(part => part.itemid === curr.itemid);
      return [...acc, findPart ? findPart : curr];
    }, []);
  };
  const getWallHeightUpdatedSections = (sections, movedParts) => {
    return sections.reduce((acc, curr) => {
      return [
        ...acc,
        {
          ...curr,
          items: replaceMovedPartsInSection(curr, movedParts),
          height: curr.height + moveInYDirection,
          localOptions: { keepParts: true },
        },
      ];
    }, []);
  };
  const wallHeightDependentItems = getWallHeightDependentItems();
  const movedWallHeightDependentItems = wallHeightDependentItems.parts.reduce(
    (acc, curr) => {
      return [...acc, getMovedWallHeightDependingItem(curr)];
    },
    []
  );
  const wallHeightDependentSectionsUpdate = getWallHeightUpdatedSections(
    wallHeightDependentItems.sections,
    movedWallHeightDependentItems
  );
  dispatch(
    thunkUpdateMultiple(wallHeightDependentSectionsUpdate, undefined, {
      automatedUpdate: true,
    })
  );
};

export const thunkSetWall =
  ({ width, height }, meta = {}) =>
  (dispatch, getState) => {
    const getCurrentWallHeight = () => {
      return selectWallSize(getState())?.height;
    };
    const getCurrentWallWidth = () => {
      return selectWallSize(getState())?.width;
    };
    const getNewWallWidth = () => {
      return width ? width : getCurrentWallWidth();
    };
    const getNewWallHeight = () => {
      return height ? height : getCurrentWallHeight();
    };
    const ceilingDidMove = () => {
      return getNewWallHeight() !== getCurrentWallHeight();
    };
    const getDifferenceInHeight = () => {
      return getNewWallHeight() - getCurrentWallHeight();
    };

    const getNewModel = model =>
      constants.DYNAMIC_WALL_LIMITS
        ? updateDependentItems(model, {
            isPersistent: meta?.isPersistent,
            wallUpdate: true,
          })
        : model;

    const currentPoints = selectWallPoints(getState());
    const newPoints = getWallPoints(currentPoints, width, height);
    const wall = {
      points: newPoints,
      size: {
        width: getNewWallWidth(),
        height: getNewWallHeight(),
      },
    };
    if (ceilingDidMove()) {
      const tac = selectTac(getState());
      const curedHeight = getCuredHeight(getNewWallHeight(), tac);
      const marketRelativeHeight = selectCeilingHeightBasedOnMarket(
        getState(),
        curedHeight
      );

      dispatch(actionSetDraftCeilingHeight(marketRelativeHeight));
      dispatch(thunkMoveWallHeightDependentItems(getDifferenceInHeight()));
    }

    const model = {
      ...selectTac(getState()),
      wall,
    };

    const newModel = getNewModel(model);
    const errors = validate(newModel);

    history.save(convert.TACtoPAC(newModel));

    dispatch(actionSetWall(newModel, errors, meta));
    meta?.isPersistent && dispatch(thunkSetMargins());
  };

export const thunkHandleErrors = () => async (dispatch, getState) => {
  const getHighestPriorityErrors = errors => {
    const priorityOfError = error => error.options.priority ?? -Infinity;
    return errors
      .sort((a, b) => priorityOfError(a) - priorityOfError(b))
      .filter(
        (error, index, allErrors) =>
          priorityOfError(error) === priorityOfError(allErrors[0])
      );
  };

  const onInteraction = () =>
    dispatch(actionSetErrorHandlingPendingTimestamp(null));

  const tac = selectTac(getState());
  const errors = tac?.errors;

  if (!errors || !errors.length) return;

  const highestPriorityErrors = getHighestPriorityErrors(errors);
  const highestPriorityErrorsAreAllOfTheSameType = highestPriorityErrors.every(
    error => error.statisticsLabel === highestPriorityErrors[0].statisticsLabel
  );
  const highestPriorityErrorsWithAmelioration = highestPriorityErrors.filter(
    error => error.getAmelioration
  );
  if (
    highestPriorityErrorsWithAmelioration.length &&
    !highestPriorityErrorsAreAllOfTheSameType
  ) {
    throw new Error(
      'Internal error. Multiple types of validation errors are listed at the same priority level as an error associated with an amelioration.'
    );
  }

  const errorWithAmelioration = highestPriorityErrorsWithAmelioration.length
    ? highestPriorityErrorsWithAmelioration[0]
    : null;
  if (errorWithAmelioration) {
    localStatisticsReporter.reportAmeliorationAttempt(
      errorWithAmelioration.statisticsLabel
    );
  }

  const solvableAmelioration = errorWithAmelioration
    ?.getAmelioration()
    .checkWhetherCanAmeliorate(tac)
    ? errorWithAmelioration.getAmelioration()
    : null;
  if (errorWithAmelioration && !solvableAmelioration) {
    localStatisticsReporter.reportAmeliorationCanNotBeSolved(
      errorWithAmelioration.statisticsLabel
    );
  }

  if (solvableAmelioration) {
    localStatisticsReporter.reportValidationError(
      errorWithAmelioration.statisticsLabel,
      MeansOfNotification.Amelioration
    );

    const ameliorationResult = await solvableAmelioration.start(tac);
    if (ameliorationResult.outcome === AmeliorationOutcome.Success) {
      const updatedTac = ameliorationResult.tacOut;
      dispatch(thunkReplaceTac(updatedTac));
      localStatisticsReporter.reportAmeliorationSolved(
        errorWithAmelioration.statisticsLabel
      );

      const timestamp = Date.now();
      dispatch(actionSetErrorHandlingPendingTimestamp(timestamp));
      window.addEventListener(POINTER_DOWN, onInteraction);
      window.addEventListener(KEY_DOWN, onInteraction);

      await toastMaster.noMorePendingToasts();
      window.removeEventListener(POINTER_DOWN, onInteraction);
      window.removeEventListener(KEY_DOWN, onInteraction);

      const selectedTimestamp = selectErrorHandlingPendingTimestamp(getState());

      if (selectedTimestamp === timestamp) {
        await dispatch(thunkHandleErrors());
      } else {
        /* Do nothing.
           When the timestamps don't match, user interaction while the amelioration
           toast was being displayed has cancelled the pending error handling process. */
      }
    } else if (ameliorationResult.outcome === AmeliorationOutcome.Declined) {
      /* Do nothing, except sending a statistics event.
         Just let the user fix the error on their own. */
      localStatisticsReporter.reportAmeliorationDeclined(
        errorWithAmelioration.statisticsLabel
      );
    } else {
      /* If we get here, it's likely that we have a bug in the amelioration
         or in this function.
         Are we sure that the amelioration's fastCheck function doesn't
         return true in scenarios where the transform fails? */
      throw new Error(
        'Internal error. Abnormal amelioration behaviour encountered.'
      );
    }
  } else {
    dispatch(actionShowSceneErrors(highestPriorityErrors));
  }
};
